import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { SafeExternalLink } from "@/components/SafeExternalLink";
import { EditorialImage } from "@/components/EditorialImage";
import { SaveButton } from "@/components/SaveButton";
import type { SavedKind } from "@/lib/saved-items.functions";
import { getMotifFallback, type MotifKind } from "@/lib/motif-fallback";
import { normalizeTitle } from "@/lib/normalize-title";

/**
 * Fælles billed-kort til opskrifter og video – 3-kolonne grid.
 * Behold samme feuilleton-sprog: kicker, serif-titel, kilde/meta.
 * Sæt `saveKind` for at få en "+ Gem"-knap på hvert kort.
 */

export type ImageCardItem = {
  id: string;
  kicker: ReactNode;
  title: string;
  description?: ReactNode;
  meta?: ReactNode;
  imageUrl: string | null;
  ratio: string;
  play?: boolean;
  duration?: string | null;
  href?: string;
  to?: string;
  params?: Record<string, string>;
  onOpen?: () => void;
  /** Kategori/topic-nøgle bruges til motif-fallback hvis billedet mangler/fejler. */
  category?: string | null;
};

function CardInner({ item, fallbackKind }: { item: ImageCardItem; fallbackKind: MotifKind }) {
  const fallback = getMotifFallback(fallbackKind, item.category);
  return (
    <>
      <EditorialImage
        src={item.imageUrl}
        alt={item.title}
        ratio={item.ratio}
        play={item.play}
        duration={item.duration ?? undefined}
        className="mb-4"
        fallback={fallback}
      />
      <span className="font-serif text-[11px] font-medium tracking-[0.18em] uppercase text-accent block mb-2">
        {item.kicker}
      </span>
      <h3 className="font-serif font-normal text-[20px] md:text-[23px] leading-[1.2] text-ink group-hover:text-accent transition-colors">
        {normalizeTitle(item.title)}
      </h3>
      {item.meta && (
        <p className="mt-2 font-serif italic text-[13px] text-muted">{item.meta}</p>
      )}
      {item.description && (
        <p className="mt-2 font-serif font-light text-[14px] md:text-[15px] leading-[1.55] text-ink-soft">
          {item.description}
        </p>
      )}
    </>
  );
}


export function ImageCardGrid({
  items,
  saveKind,
}: {
  items: ImageCardItem[];
  /** Slå "+ Gem"-knap til på hvert kort ved at angive typen. */
  saveKind?: SavedKind;
}) {
  if (items.length === 0) return null;
  const fallbackKind: MotifKind = saveKind ?? "generic";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-9">
      {items.map((item) => {
        const cls =
          "fl-card group flex flex-col focus:outline focus:outline-2 focus:outline-accent focus:-outline-offset-4";
        const linkNode = item.to ? (
          <Link
            to={item.to}
            params={item.params as never}
            onClick={item.onOpen}
            className={cls}
          >
            <CardInner item={item} fallbackKind={fallbackKind} />
          </Link>
        ) : (
          <SafeExternalLink
            href={item.href ?? "#"}
            onOpen={item.onOpen}
            className={cls}
          >
            <CardInner item={item} fallbackKind={fallbackKind} />
          </SafeExternalLink>
        );
        if (!saveKind) {
          return <div key={item.id}>{linkNode}</div>;
        }
        return (
          <div key={item.id} className="flex flex-col">
            {linkNode}
            <div className="mt-3 flex justify-start">
              <SaveButton id={item.id} kind={saveKind} compact />
            </div>
          </div>
        );
      })}
    </div>
  );
}

